import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useId } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import {
  BRACKET_CORNERS,
  DOT_GRID,
  MICRO_LABEL,
  PILL_TONE,
  PRIMARY_THUMB,
  SECONDARY_THUMB,
} from './recipes'

export interface HumintNoticeCopy {
  /** Micro line above the title, e.g. the section name and a "soon" pill. */
  eyebrow?: ReactNode
  title: string
  body: ReactNode
}

export interface HumintNoticeAction {
  label: string
  Icon?: LucideIcon
  onClick: () => void
  /** `secondary` when the shell already offers the same way on (e.g. the check tab). */
  emphasis?: 'primary' | 'secondary'
  /** The action is on its way (a read made again): the button waits for it. */
  loading?: boolean
}

export interface HumintNoticeProps {
  Icon: LucideIcon
  copy: HumintNoticeCopy
  /** The one way on, in the thumb zone on the phone. */
  action?: HumintNoticeAction
  /** A second way on, drawn as an outline button beside (desktop) or under (phone) `action`. */
  secondaryAction?: HumintNoticeAction
  /** `warning` for a refusal (not a member of the case), `neutral` for "not here yet". */
  tone?: 'neutral' | 'warning'
}

const MARK_TONE = {
  neutral: 'border-border bg-background text-muted-foreground',
  warning: PILL_TONE.warning,
} as const

/** The icon inside a dotted viewfinder tile with copper brackets: the workspace's own mark. */
function NoticeMark({ Icon, tone }: { Icon: LucideIcon; tone: keyof typeof MARK_TONE }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative flex size-24 shrink-0 items-center justify-center rounded-2xl border border-border bg-card @5xl/humint:size-20 @5xl/humint:rounded-xl',
        DOT_GRID
      )}
    >
      <span className="absolute inset-2.5">
        {BRACKET_CORNERS.map(corner => (
          <span key={corner} className={cn('absolute size-4 border-primary', corner)} />
        ))}
      </span>
      <span
        className={cn(
          'flex size-12 items-center justify-center rounded-xl border @5xl/humint:size-10',
          MARK_TONE[tone]
        )}
      >
        <Icon className="size-6 @5xl/humint:size-5" />
      </span>
    </span>
  )
}

function NoticeButton({ action }: { action: HumintNoticeAction }) {
  const primary = action.emphasis !== 'secondary'
  return (
    <Button
      variant={primary ? 'solid' : 'outline'}
      size={primary ? '2xl' : 'xl'}
      className={cn(primary ? PRIMARY_THUMB : SECONDARY_THUMB, '@5xl/humint:self-start')}
      onClick={action.onClick}
      loading={action.loading}
    >
      {action.Icon && !action.loading && (
        <action.Icon aria-hidden className="size-5 @5xl/humint:size-4" />
      )}
      {action.label}
    </Button>
  )
}

/**
 * A whole-screen notice inside the shell's `main`: why this screen cannot do its job, and the
 * one way on. Phone: the words centred in the free space, the action pinned to the thumb
 * zone. Desktop (`@5xl/humint`): a left-aligned column with the action under the words.
 */
export function HumintNotice({
  Icon,
  copy,
  action,
  secondaryAction,
  tone = 'neutral',
}: HumintNoticeProps) {
  const titleId = useId()
  return (
    <section
      aria-labelledby={titleId}
      className="flex min-h-0 flex-1 flex-col gap-6 p-4 @5xl/humint:mx-auto @5xl/humint:w-full @5xl/humint:max-w-xl @5xl/humint:justify-center @5xl/humint:p-6"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center @5xl/humint:flex-none @5xl/humint:items-start @5xl/humint:text-left">
        <NoticeMark Icon={Icon} tone={tone} />
        <div className="flex max-w-sm flex-col gap-2 @5xl/humint:max-w-none">
          {copy.eyebrow && (
            <div
              className={cn(
                MICRO_LABEL,
                'flex items-center justify-center gap-2 @5xl/humint:justify-start'
              )}
            >
              {copy.eyebrow}
            </div>
          )}
          <h2
            id={titleId}
            className="text-xl leading-7 font-bold tracking-[-0.01em] @5xl/humint:text-2xl @5xl/humint:leading-8"
          >
            {copy.title}
          </h2>
          <p className="text-[15px] leading-5 text-muted-foreground @5xl/humint:text-sm">
            {copy.body}
          </p>
        </div>
      </div>
      {secondaryAction ? (
        <div className="flex flex-col gap-2 @5xl/humint:flex-row @5xl/humint:self-start">
          {action && <NoticeButton action={action} />}
          <NoticeButton action={{ ...secondaryAction, emphasis: 'secondary' }} />
        </div>
      ) : (
        action && <NoticeButton action={action} />
      )}
    </section>
  )
}
